import { useCallback } from "react";
import {
  useChatValues,
  useChatIsStreaming,
  useStreamSelector,
} from "@launch10/use-langgraph-stream";
import type { Artifact } from "@shared";

/**
 * The chat-side half of the artifact loop, shared by every surface
 * (brainstorm, ads, …): read the `artifacts` channel off the stream, and
 * approve by submitting the reviewed artifact back as a state-only run —
 * it folds through the artifacts reducer and triggers that surface's
 * review reminder. Use with `assignArtifactsToMessages` to anchor each
 * artifact at its surfacing tool-message, and `ArtifactRenderer` to draw
 * it. Must be used inside Chat.Root (SmartStreamProvider).
 */
export function useChatArtifacts(): {
  artifacts: Artifact[] | undefined;
  onApprove: (artifact: Artifact) => void;
  disabled: boolean;
} {
  const values = useChatValues() as { artifacts?: Artifact[] } | undefined;
  const disabled = useChatIsStreaming();
  const submit = useStreamSelector((s) => s.submit);

  const onApprove = useCallback(
    (artifact: Artifact) => {
      submit({ artifacts: [artifact] });
    },
    [submit],
  );

  return { artifacts: values?.artifacts, onApprove, disabled };
}
